import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { auth } from '../../firebase';
import { apiUrl } from '../../utils/apiBase';
import { rejectDeprecatedTestTrendyolBrandId } from '../../utils/trendyolBrandIds';
import {
  buildTrendyolProductDefaults,
  normalizeTrendyolAttributes,
  normalizeTrendyolProductInfo,
  TRENDYOL_CARGO_COMPANY_LABEL_BY_ID,
  TRENDYOL_CARGO_COMPANY_SELECT_IDS,
  TRENDYOL_DELIVERY_DURATION_OPTIONS,
  TRENDYOL_VAT_RATE_OPTIONS,
  validateTrendyolProductReadiness
} from './trendyolProductFormUtils';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#fff',
    fontSize: '0.96rem'
  },
  '& .MuiInputLabel-root': {
    fontWeight: 700,
    color: '#64748b'
  },
  '& .MuiSelect-select': {
    minHeight: '1.45em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'clip'
  }
};

const getBackendAuthHeaders = async () => {
  const headers = {};
  const sharedSecret = String(process.env.REACT_APP_CAMPAIGN_AI_SHARED_SECRET || '').trim();
  if (sharedSecret) headers['x-ukba-internal-key'] = sharedSecret;
  try {
    const token = await auth.currentUser?.getIdToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (error) {
    // Backend token yoksa mevcut güvenlik kurallarına göre isteği reddeder.
  }
  return headers;
};

const toText = (value) => String(value ?? '').trim();

const formatCategoryId = (value) => toText(value);

const normalizeSearchText = (value) =>
  toText(value)
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const dedupeCategories = (rows = []) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = `${formatCategoryId(row?.id)}::${normalizeSearchText(row?.path || row?.name)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const buildCategoryPath = (node, parentPath = '') => {
  const name = toText(node?.name);
  const currentPath = toText(node?.path || node?.categoryPath || node?.displayName);
  if (currentPath) return currentPath;
  return [parentPath, name].filter(Boolean).join(' > ');
};

const flattenCategories = (rows = [], parentPath = '') =>
  (Array.isArray(rows) ? rows : []).flatMap((node) => {
    const id = formatCategoryId(node?.id);
    const name = toText(node?.name);
    const path = buildCategoryPath(node, parentPath);
    const children = Array.isArray(node?.subCategories) ? node.subCategories : [];
    const isLeaf = node?.isLeaf === true || children.length === 0;
    const item = id || name ? [{ id, name, path: path || name, isLeaf }] : [];
    return [...item, ...flattenCategories(children, path || name)];
  });

const extractCategoryRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];

  const candidates = [
    payload.categories,
    payload.data,
    payload.result,
    payload.items,
    payload.content,
    payload.raw?.categories,
    payload.raw?.data,
    payload.raw?.result,
    payload.raw?.items,
    payload.raw?.content
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    if (candidate && typeof candidate === 'object') {
      const nested = extractCategoryRows(candidate);
      if (nested.length) return nested;
    }
  }

  return [];
};

const normalizeCategoryPayload = (payload) => dedupeCategories(flattenCategories(extractCategoryRows(payload)));

const filterCategoriesLocally = (rows = [], query = '') => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return rows;
  return rows.filter((row) => {
    const haystack = normalizeSearchText([row?.name, row?.path, row?.id].filter(Boolean).join(' '));
    return haystack.includes(normalizedQuery);
  });
};

const getCategoryOptionLabel = (option) => {
  if (!option) return '';
  const name = toText(option.name);
  const id = toText(option.id);
  return id ? `${name} (${id})` : name;
};

const readJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

const getAttributeValues = (attribute) =>
  Array.isArray(attribute?.attributeValues) ? attribute.attributeValues.filter(Boolean) : [];

const getAttributeValuePreview = (attribute, limit = 4) =>
  getAttributeValues(attribute)
    .slice(0, limit)
    .map((value) => toText(value?.name || value?.value || value?.id))
    .filter(Boolean);

const attributeKey = (attribute) => toText(attribute?.attributeId);

const valueKey = (value) => toText(value?.id);

/** Ürün Ekle uyarı şeridi: yalnızca kullanıcıdan beklenen eksikler (otomatik/kayıtta dolacaklar ve utils’ten gelen fazlalıklar gösterilmez) */
const URUN_EKLE_READINESS_ALLOWED_FIELD_LABELS = new Set([
  'Marka ID',
  'Trendyol kategori',
  'KDV oranı',
  'Desi',
  'Kargo firması ID'
]);

/** Ürün Ekle başlık satırında veya form gövdesinde paylaşılan hazırlık özeti (yalnızca UI). */
export function TrendyolReadinessSummary({
  readiness,
  isInline = true,
  showValidation = true,
  denseBar = false,
  suppressReadyDisclaimer = false,
  emphasizedTitle = false,
  /** Tüm eksik kalemleri listele (+N daha yok); ürün ekle uyarı şeridi için kullanılır */
  expandMissingChips = false
}) {
  if (!showValidation || !readiness) return null;
  void expandMissingChips;

  if (denseBar) {
    const allMissing = Array.isArray(readiness.missingFields) ? readiness.missingFields : [];
    return (
      <Alert
        severity={readiness.ready ? 'success' : 'warning'}
        sx={{
          borderRadius: 1.5,
          py: 0.4,
          px: 0.85,
          alignItems: 'center',
          '& .MuiAlert-icon': {
            py: 0,
            mr: 0.65,
            alignSelf: 'center',
            fontSize: '1.05rem',
            opacity: 0.95
          },
          '& .MuiAlert-message': { width: '100%', minWidth: 0, py: 0, overflow: 'hidden' }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 0.72,
            rowGap: 0.38,
            width: '100%',
            maxWidth: '100%'
          }}
        >
          <Typography
            component="span"
            sx={{
              fontWeight: 900,
              fontSize: emphasizedTitle ? '0.805rem' : '0.785rem',
              lineHeight: 1.22,
              flexShrink: 0,
              color: 'inherit'
            }}
          >
            {readiness.ready ? 'Trendyol gönderimine hazır' : `Trendyol için ${readiness.missingCount} eksik bilgi var`}
          </Typography>
          {!readiness.ready ? (
            <>
              {allMissing.map((field, idx) => (
                <Chip
                  key={`${field.type}-${field.label}-${idx}`}
                  size="small"
                  label={field.label}
                  sx={{
                    fontWeight: 700,
                    fontSize: emphasizedTitle ? '0.695rem' : '0.675rem',
                    height: 'auto',
                    minHeight: 19,
                    maxWidth: '100%',
                    py: 0.15,
                    '& .MuiChip-label': {
                      px: 0.82,
                      py: 0.05,
                      whiteSpace: 'normal',
                      lineHeight: 1.25,
                      overflowWrap: 'anywhere'
                    }
                  }}
                />
              ))}
            </>
          ) : !suppressReadyDisclaimer ? (
            <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.25, opacity: 0.95 }}>
              Bu ekranda ürün Trendyol’a gönderilmez.
            </Typography>
          ) : null}
        </Box>
      </Alert>
    );
  }

  return (
    <Alert
      severity={readiness.ready ? 'success' : 'warning'}
      sx={{
        borderRadius: 2,
        alignItems: 'flex-start',
        py: isInline ? 0.65 : undefined,
        px: isInline ? 1 : undefined,
        '& .MuiAlert-message': { width: '100%', minWidth: 0 }
      }}
    >
      <Stack spacing={0.65}>
        <Typography sx={{ fontWeight: 900, fontSize: emphasizedTitle ? '1rem' : isInline ? '0.82rem' : undefined, lineHeight: 1.35 }}>
          {readiness.ready ? 'Trendyol gönderimine hazır' : `Trendyol için ${readiness.missingCount} eksik bilgi var`}
        </Typography>
        {!readiness.ready ? (
          <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap sx={{ gap: 0.4 }}>
            {readiness.missingFields.map((field, idx) => (
              <Chip
                key={`${field.type}-${field.label}-${idx}`}
                size="small"
                label={field.label}
                sx={{ fontWeight: 700, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.25 } }}
              />
            ))}
          </Stack>
        ) : !suppressReadyDisclaimer ? (
          <Typography sx={{ color: '#166534', fontSize: isInline ? '0.76rem' : '0.86rem', fontWeight: 700, lineHeight: 1.4 }}>
            Bu kontrol bilgi amaçlıdır; bu ekranda ürün Trendyol’a gönderilmez.
          </Typography>
        ) : null}
      </Stack>
    </Alert>
  );
}

const sections = [
  {
    title: 'Temel Ürün Bilgileri',
    description: 'Trendyol katalog kaydı için ürünün ana kimlik bilgileri.',
    fields: [
      { name: 'ty_title', label: 'Trendyol ürün adı', wide: true },
      { name: 'ty_barcode', label: 'Barkod' },
      { name: 'ty_stockCode', label: 'Stok kodu' },
      { name: 'ty_productMainId', label: 'Ana ürün kodu' }
    ]
  },
  {
    title: 'Marka & Kategori',
    description: 'Trendyol kategorisini arayarak seçin; seçilen kategori ürün kaydına yazılır.',
    fields: [
      { name: 'ty_brandName', label: 'Marka adı' },
      { name: 'ty_brandId', label: 'Marka ID' }
    ]
  },
  {
    title: 'Gönderim Bilgileri',
    description: 'Pazaryeri gönderiminde kullanılacak operasyonel bilgiler.',
    fields: [
      { name: 'ty_vatRate', label: 'KDV', select: true, options: TRENDYOL_VAT_RATE_OPTIONS },
      { name: 'ty_dimensionalWeight', label: 'Desi', type: 'number' },
      { name: 'ty_cargoCompanyId', label: 'Kargo firması', select: true, options: TRENDYOL_CARGO_COMPANY_SELECT_IDS },
      { name: 'ty_deliveryDuration', label: 'Teslimat', select: true, options: TRENDYOL_DELIVERY_DURATION_OPTIONS }
    ]
  }
];

const TRIPLE_ROW_SECTION_TITLES = ['Temel Ürün Bilgileri', 'Marka & Kategori', 'Gönderim Bilgileri'];

function TrendyolProductInfoForm({
  value,
  onChange,
  product = {},
  showValidation = true,
  variant = 'default',
  hideAutoFields = false,
  externalReadinessBanner = false,
  onReadinessChange,
  mergedCategoryShipmentLayout = false,
  trendyolCategorySearchPortalEl = null,
  /** Ürün listesi modalı: Temel bilgiler, Marka & Kategori, Gönderim yan yana */
  tripleColumnSectionsLayout = false,
  /** Gönderim (KDV, desi, kargo firması, teslimat süresi) alanları bu düğümün içinde portalla gösterilir; kart üstündeki bölüm gizlenir */
  trendyolShipmentFieldsPortalEl = null,
  /** Ürün açıklaması ana modalda olduğunda Trendyol açıklaması alanını gizle */
  hideTrendyolDescription = false,
  /** Ürün Ekle sayfası: gereksiz alanlar gizlenmiş sade inline deneyim */
  urunEkleSimplifiedTrendyol = false,
  /** Ürün ekle: Marka ID genel bilgilerdeyse kartta tekrarlama */
  suppressBrandIdField = false,
  /** Ürün listesi Trendyol modalı vb.: zorunlu özellikleri Ürün Ekle sayfasındaki gibi kompakt kutularda göster */
  compactMandatoryAttributes = false
}) {
  const isInline = variant === 'inline';
  const hideAuto = Boolean(hideAutoFields);
  const compactUrunEkle = Boolean(isInline && hideAuto);
  const mergedTY = Boolean(compactUrunEkle && mergedCategoryShipmentLayout);
  /** Modal / üst form: Ürün Ekle ile aynı sıkı zorunlu özellik ızgarası */
  const compactMandatoryAttrLayout = Boolean(compactUrunEkle || compactMandatoryAttributes);
  /** Kompakt özellik kartı tipografisi (+ input sıkılığı): ürün ekle veya compactMandatoryAttributes */
  const slimAttributeCards = Boolean(urunEkleSimplifiedTrendyol || compactMandatoryAttributes);
  /** Ürün Ekle: zorunlu özellikler kategori kutusunun yanında yatay şeritte */
  const inlineTyAttrsBesideCategory = Boolean(mergedTY);
  const [configDefaults, setConfigDefaults] = useState({});
  const sectionsToRender = useMemo(() => {
    if (!hideAuto) return sections;
    return sections
      .filter((s) => s.title !== 'Temel Ürün Bilgileri')
      .map((s) => {
        if (s.title === 'Marka & Kategori') {
          return {
            ...s,
            title: 'Trendyol kategorisi',
            description: '',
            fields: []
          };
        }
        return s;
      });
  }, [hideAuto]);
  const sectionsForMap = useMemo(() => {
    let list =
      mergedTY
        ? sectionsToRender.filter((s) => s.title !== 'Trendyol kategorisi' && s.title !== 'Gönderim Bilgileri')
        : sectionsToRender;
    if (trendyolCategorySearchPortalEl) {
      list = list.filter((s) => s.title !== 'Marka & Kategori' && s.title !== 'Trendyol kategorisi');
    }
    if (trendyolShipmentFieldsPortalEl) {
      list = list.filter((s) => s.title !== 'Gönderim Bilgileri');
    }
    return list;
  }, [mergedTY, sectionsToRender, trendyolCategorySearchPortalEl, trendyolShipmentFieldsPortalEl]);

  const tripleCatalogRowSections = useMemo(() => {
    if (!tripleColumnSectionsLayout || mergedTY) return [];
    return TRIPLE_ROW_SECTION_TITLES.map((t) => sectionsForMap.find((s) => s.title === t)).filter(Boolean);
  }, [tripleColumnSectionsLayout, mergedTY, sectionsForMap]);

  const useTripleCatalogRow =
    Boolean(
      tripleColumnSectionsLayout &&
        !mergedTY &&
        tripleCatalogRowSections.length === TRIPLE_ROW_SECTION_TITLES.length
    );
  const productWithDefaults = useMemo(
    () => ({
      ...configDefaults,
      ...product
    }),
    [configDefaults, product]
  );
  const normalized = normalizeTrendyolProductInfo(value, productWithDefaults);
  const defaults = buildTrendyolProductDefaults(productWithDefaults);
  const [categorySearch, setCategorySearch] = useState(normalized.ty_categoryName || '');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [attributeLoading, setAttributeLoading] = useState(false);
  const [attributeError, setAttributeError] = useState('');
  const [categoryAttributes, setCategoryAttributes] = useState([]);
  const selectedAttributes = useMemo(
    () => normalizeTrendyolAttributes(value?.ty_attributes ?? product?.ty_attributes),
    [product?.ty_attributes, value?.ty_attributes]
  );

  const selectedCategory = useMemo(() => {
    const id = formatCategoryId(normalized.ty_categoryId);
    if (!id && !normalized.ty_categoryName) return null;
    return {
      id,
      name: normalized.ty_categoryName || normalized.ty_categoryPath || id,
      path: normalized.ty_categoryPath || normalized.ty_categoryName || id,
      isLeaf: true
    };
  }, [normalized.ty_categoryId, normalized.ty_categoryName, normalized.ty_categoryPath]);
  const attributeSummary = useMemo(() => {
    const rows = Array.isArray(categoryAttributes) ? categoryAttributes : [];
    return {
      total: rows.length,
      valueListCount: rows.filter((attribute) => getAttributeValues(attribute).length > 0).length,
      customAllowedCount: rows.filter((attribute) => attribute?.allowCustom === true).length
    };
  }, [categoryAttributes]);
  const missingRequiredAttributes = useMemo(
    () =>
      (Array.isArray(categoryAttributes) ? categoryAttributes : []).filter((attribute) => {
        const selected = selectedAttributes.find((item) => toText(item.attributeId) === attributeKey(attribute));
        return attribute?.allowCustom
          ? !toText(selected?.customAttributeValue)
          : !toText(selected?.attributeValueId);
      }),
    [categoryAttributes, selectedAttributes]
  );

  /** Zorunlu özellikler: satır başına en fazla 10 eşit genişlikte kutu */
  const mandatoryAttributeChunks = useMemo(() => {
    const list = Array.isArray(categoryAttributes) ? categoryAttributes : [];
    const chunks = [];
    for (let i = 0; i < list.length; i += 10) {
      chunks.push(list.slice(i, i + 10));
    }
    return chunks;
  }, [categoryAttributes]);

  useEffect(() => {
    let cancelled = false;
    const loadConfigDefaults = async () => {
      try {
        const response = await fetch(apiUrl('/api/trendyol/config'), {
          method: 'GET',
          headers: await getBackendAuthHeaders()
        });
        const json = await readJsonSafely(response);
        if (!response.ok || json?.success === false) return;
        const config = json?.config && typeof json.config === 'object' ? json.config : {};
        if (cancelled) return;
        setConfigDefaults({
          defaultBrandId: toText(config.defaultBrandId),
          defaultVatRate: toText(config.defaultVatRate),
          defaultDimensionalWeight: toText(config.defaultDimensionalWeight),
          defaultCargoCompanyId: toText(config.defaultCargoCompanyId),
          defaultDeliveryDuration: toText(config.defaultDeliveryDuration)
        });
      } catch (error) {
        // Config defaultları yoksa ürün özel alanlarıyla devam edilir.
      }
    };
    loadConfigDefaults();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const defaultPatch = {};
    [
      ['ty_brandId', defaults.ty_brandId],
      ['ty_vatRate', defaults.ty_vatRate],
      ['ty_dimensionalWeight', defaults.ty_dimensionalWeight],
      ['ty_deliveryDuration', defaults.ty_deliveryDuration],
      ['ty_cargoCompanyId', defaults.ty_cargoCompanyId]
    ].forEach(([field, defaultValue]) => {
      const defTxt = toText(defaultValue);
      if (!defTxt) return;
      if (field === 'ty_brandId') {
        const cur = rejectDeprecatedTestTrendyolBrandId(toText(value?.[field]));
        if (!cur) defaultPatch[field] = defTxt;
        return;
      }
      if (!toText(value?.[field])) {
        defaultPatch[field] = defTxt;
      }
    });
    if (Object.keys(defaultPatch).length > 0) {
      onChange?.({
        ...normalized,
        ty_attributes: selectedAttributes,
        ...defaultPatch
      });
    }
  }, [
    defaults.ty_brandId,
    defaults.ty_cargoCompanyId,
    defaults.ty_deliveryDuration,
    defaults.ty_dimensionalWeight,
    defaults.ty_vatRate,
    normalized,
    onChange,
    selectedAttributes,
    value
  ]);

  const readiness = useMemo(
    () =>
      validateTrendyolProductReadiness(normalized, productWithDefaults, {
        requiredAttributes: categoryAttributes,
        hasProductImage: productWithDefaults?.hasTrendyolProductImage
      }),
    [categoryAttributes, normalized, productWithDefaults]
  );

  const readinessForBanner = useMemo(() => {
    if (!urunEkleSimplifiedTrendyol) return readiness;
    const missingFields = readiness.missingFields
      .map((item) =>
        item.type === 'image'
          ? { ...item, label: 'En az 1 Trendyol görseli' }
          : item
      )
      .filter((item) => {
        if (item.type === 'attribute') return true;
        if (item.type === 'image') return true;
        if (item.type === 'field') return URUN_EKLE_READINESS_ALLOWED_FIELD_LABELS.has(item.label);
        return false;
      });
    return {
      ...readiness,
      missingFields,
      missingCount: missingFields.length,
      ready: missingFields.length === 0
    };
  }, [readiness, urunEkleSimplifiedTrendyol]);

  useEffect(() => {
    if (!externalReadinessBanner || !onReadinessChange) return;
    onReadinessChange(readinessForBanner);
  }, [externalReadinessBanner, onReadinessChange, readinessForBanner]);

  useEffect(() => {
    const query = toText(categorySearch);
    if (query.length < 2) {
      setCategoryOptions([]);
      setCategoryError('');
      setCategoryLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setCategoryLoading(true);
      setCategoryError('');
      try {
        const fetchCategoryOptions = async (requestPath) => {
          const response = await fetch(apiUrl(requestPath), {
            method: 'GET',
            headers: await getBackendAuthHeaders()
          });
          const json = await readJsonSafely(response);
          if (!response.ok || json?.success === false) {
            const error = new Error(json?.error || json?.message || 'Kategori bilgileri alınamadı.');
            error.status = json?.status || json?.trendyolStatus || response.status;
            error.hint = json?.hint || '';
            throw error;
          }
          return normalizeCategoryPayload(json);
        };

        let flattened = await fetchCategoryOptions(`/api/trendyol/categories?name=${encodeURIComponent(query)}`);

        const asciiQuery = normalizeSearchText(query);
        if (!flattened.length && asciiQuery && asciiQuery !== query) {
          flattened = await fetchCategoryOptions(`/api/trendyol/categories?name=${encodeURIComponent(asciiQuery)}`);
        }

        if (!flattened.length) {
          const allCategories = await fetchCategoryOptions('/api/trendyol/categories');
          flattened = filterCategoriesLocally(allCategories, query);
        }

        if (!cancelled) setCategoryOptions(flattened);
      } catch (error) {
        if (!cancelled) {
          setCategoryOptions([]);
          const statusText = error?.status ? ` Backend ${error.status} döndü.` : '';
          setCategoryError(`Kategori bilgileri alınamadı.${statusText}`);
        }
      } finally {
        if (!cancelled) setCategoryLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [categorySearch]);

  useEffect(() => {
    const categoryId = formatCategoryId(normalized.ty_categoryId);
    if (!categoryId) {
      setCategoryAttributes([]);
      setAttributeError('');
      setAttributeLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadAttributes = async () => {
      setAttributeLoading(true);
      setAttributeError('');
      try {
        const response = await fetch(apiUrl(`/api/trendyol/categories/${encodeURIComponent(categoryId)}/attributes`), {
          method: 'GET',
          headers: await getBackendAuthHeaders()
        });
        const json = await readJsonSafely(response);
        if (!response.ok || json?.success === false) {
          throw new Error(json?.message || json?.error || 'Kategori bilgileri alınamadı.');
        }
        const rows = Array.isArray(json?.required)
          ? json.required
          : Array.isArray(json?.attributes)
            ? json.attributes.filter((item) => item?.required)
            : [];
        if (!cancelled) setCategoryAttributes(rows);
      } catch (error) {
        if (!cancelled) {
          setCategoryAttributes([]);
          setAttributeError('Kategori bilgileri alınamadı.');
        }
      } finally {
        if (!cancelled) setAttributeLoading(false);
      }
    };

    loadAttributes();
    return () => {
      cancelled = true;
    };
  }, [normalized.ty_categoryId]);

  const buildNextValue = (patch) => ({
    ...normalized,
    ty_attributes: selectedAttributes,
    ...patch
  });

  const handleFieldChange = (event) => {
    const { name, value: nextValue } = event.target;
    onChange?.(buildNextValue({ [name]: nextValue }));
  };

  const handleAttributeChange = (attribute, patch) => {
    const key = attributeKey(attribute);
    if (!key) return;
    const current = selectedAttributes.find((item) => toText(item.attributeId) === key) || {};
    const nextItem = {
      attributeId: attribute.attributeId,
      attributeName: toText(attribute.attributeName),
      attributeValueId: null,
      attributeValueName: '',
      customAttributeValue: '',
      ...current,
      ...patch
    };
    const nextAttributes = [
      ...selectedAttributes.filter((item) => toText(item.attributeId) !== key),
      nextItem
    ].filter((item) => toText(item.attributeValueId) || toText(item.attributeValueName) || toText(item.customAttributeValue));
    onChange?.({
      ...normalized,
      ty_attributes: nextAttributes
    });
  };

  const handleCategorySelect = (category) => {
    if (!category || !category.isLeaf) return;
    const nextCategory = {
      ty_categoryId: formatCategoryId(category.id),
      ty_categoryName: toText(category.name),
      ty_categoryPath: toText(category.path || category.name)
    };
    setCategorySearch(nextCategory.ty_categoryName);
    onChange?.({
      ...normalized,
      ...nextCategory,
      ty_attributes: []
    });
  };

  const renderSelectValue = (field, selected) => {
    if (field.name === 'ty_cargoCompanyId') {
      const raw = selected != null && selected !== '' ? String(selected).trim() : '';
      const key = /^\d+$/.test(raw) ? String(Number(raw)) : raw;
      return key ? TRENDYOL_CARGO_COMPANY_LABEL_BY_ID[key] || `ID ${key}` : '';
    }
    if (selected === '' || selected === null || selected === undefined) return '';
    return field.name === 'ty_deliveryDuration' ? `${selected} gün` : `%${selected}`;
  };

  const sectionShellSx = isInline
    ? {
        p: compactUrunEkle ? { xs: 0.52, sm: 0.62 } : { xs: 1, sm: 1.15 },
        borderRadius: 2,
        bgcolor: '#ffffff',
        border: '1px solid #e8e4dc',
        boxShadow: 'none'
      }
    : {
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)'
      };

  const mergedCategoryGonderimShellSx =
    mergedTY && isInline
      ? {
          ...sectionShellSx,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          alignSelf: 'stretch'
        }
      : null;

  const fieldGridSpacing = compactUrunEkle ? 1 : isInline ? 1.25 : 2.25;
  const stackGap = compactUrunEkle ? 0.62 : isInline ? 1.5 : 3;
  const fieldSize = isInline ? 'small' : 'medium';

  const compactTrendyolInputSx = compactUrunEkle || compactMandatoryAttributes
    ? {
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          bgcolor: '#fff',
          fontSize: '0.875rem',
          minHeight: 38,
          py: 0
        },
        '& .MuiOutlinedInput-input': {
          py: '7.5px',
          fontSize: '0.875rem'
        },
        '& .MuiInputLabel-root': {
          fontWeight: 700,
          color: '#64748b',
          fontSize: '0.74rem'
        },
        '& .MuiSelect-select': {
          minHeight: '1.35em',
          py: '7.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }
      }
    : {};

  const renderFieldInner = (field, { gonderimTight = false, portalSmall = false } = {}) => (
    <TextField
      fullWidth
      size={portalSmall ? 'small' : fieldSize}
      select={Boolean(field.select)}
      type={field.select ? undefined : field.type || 'text'}
      label={field.label}
      name={field.name}
      value={normalized[field.name] || ''}
      onChange={handleFieldChange}
      placeholder={defaults[field.name] || ''}
      InputLabelProps={
        portalSmall
          ? {
              sx: {
                fontSize: '0.74rem',
                fontWeight: 700,
                lineHeight: 1.25,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                maxWidth: '100%'
              }
            }
          : gonderimTight
          ? {
              sx: {
                fontSize: '0.68rem',
                fontWeight: 800,
                lineHeight: 1.18,
                whiteSpace: 'normal',
                wordBreak: 'break-word'
              }
            }
          : compactMandatoryAttrLayout
            ? {
                sx: {
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  lineHeight: 1.25,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word'
                }
              }
            : undefined
      }
      sx={{
        ...inputSx,
        ...compactTrendyolInputSx,
        minWidth: 0,
        '& .MuiOutlinedInput-root': {
          minWidth: 0,
          ...(gonderimTight ? { fontSize: '0.8rem' } : {})
        },
        ...(portalSmall
          ? {
              '& .MuiOutlinedInput-notchedOutline legend': {
                maxWidth: '100%'
              }
            }
          : {}),
        ...(gonderimTight
          ? {
              '& .MuiInputBase-input': { py: 0.85 },
              '& .MuiSelect-select': { fontSize: '0.8rem', py: 0.85 }
            }
          : {})
      }}
      SelectProps={
        field.select
          ? {
              renderValue: (selected) => renderSelectValue(field, selected),
              MenuProps: { PaperProps: { sx: { maxHeight: 320 } } }
            }
          : undefined
      }
    >
      {field.select
        ? field.options.map((option) => (
            <MenuItem key={option} value={option}>
              {renderSelectValue(field, option)}
            </MenuItem>
          ))
        : null}
    </TextField>
  );

  const renderField = (field) => (
    <Grid item xs={12} sm={field.wide ? 12 : 6} md={field.wide ? 12 : 6} key={field.name} sx={{ minWidth: 0 }}>
      {renderFieldInner(field)}
    </Grid>
  );

  const renderGonderimField = (field) => (
    <Grid
      item
      xs={12}
      sm={6}
      md={3}
      key={field.name}
      sx={{ minWidth: 0, display: 'flex', flexGrow: { md: 1 }, flexBasis: { md: 0 } }}
    >
      {renderFieldInner(field, { gonderimTight: mergedTY })}
    </Grid>
  );

  const renderTrendyolFieldSectionCard = (section) => (
    <Box
      sx={{
        ...sectionShellSx,
        ...(tripleColumnSectionsLayout && !mergedTY ? { height: '100%', display: 'flex', flexDirection: 'column' } : {})
      }}
    >
      <Stack spacing={isInline ? (compactUrunEkle ? 1.05 : 1.35) : 2.25} sx={{ flex: tripleColumnSectionsLayout && !mergedTY ? 1 : undefined }}>
            <Box>
              <Typography
                sx={{
                  fontWeight: 950,
                  color: '#0f172a',
                  fontSize: isInline ? '0.9rem' : tripleColumnSectionsLayout && !mergedTY ? '0.92rem' : '1rem'
                }}
              >
                {section.title}
              </Typography>
              {section.description ? (
                !isInline ? (
                  <Typography sx={{ color: '#64748b', fontSize: tripleColumnSectionsLayout && !mergedTY ? '0.78rem' : '0.84rem', mt: 0.35, lineHeight: 1.45 }}>
                    {section.description}
                  </Typography>
                ) : (
                  <Typography sx={{ color: '#64748b', fontSize: compactUrunEkle ? '0.72rem' : '0.76rem', mt: 0.25, lineHeight: 1.4 }}>
                    {section.description}
                  </Typography>
                )
              ) : null}
            </Box>
            <Divider sx={{ my: isInline ? -0.25 : 0 }} />
            {section.title === 'Gönderim Bilgileri' ? (
              <Grid container spacing={fieldGridSpacing} sx={{ width: '100%', m: 0 }}>
                {section.fields.map((field) => renderGonderimField(field))}
              </Grid>
            ) : (
              <Grid container spacing={fieldGridSpacing}>
                {section.fields.map(renderField)}
              </Grid>
            )}
            {!trendyolCategorySearchPortalEl && (section.title === 'Marka & Kategori' || section.title === 'Trendyol kategorisi') ? (
              compactUrunEkle ? (
                <Stack spacing={1} sx={{ width: '100%', minWidth: 0, m: 0 }}>
                  <Autocomplete
                        fullWidth
                        options={categoryOptions}
                        value={selectedCategory}
                        inputValue={categorySearch}
                        loading={categoryLoading}
                        filterOptions={(options) => options}
                        getOptionLabel={getCategoryOptionLabel}
                        isOptionEqualToValue={(option, selected) => formatCategoryId(option?.id) === formatCategoryId(selected?.id)}
                        getOptionDisabled={(option) => option?.isLeaf === false}
                        onInputChange={(event, nextInput, reason) => {
                          if (reason !== 'reset') setCategorySearch(nextInput);
                        }}
                        onChange={(event, nextValue) => handleCategorySelect(nextValue)}
                        noOptionsText={
                          categoryError
                            ? 'Arama yapılamadı'
                            : categorySearch.length < 2
                              ? 'Aramak için en az 2 karakter yazın.'
                              : 'Kategori bulunamadı.'
                        }
                        loadingText="Kategoriler aranıyor..."
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Trendyol kategorisi"
                            placeholder="Örn: bilezik, kolye, yüzük"
                            size={fieldSize}
                            sx={inputSx}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {categoryLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                        renderOption={(props, option) => {
                          const { key, ...optionProps } = props;
                          return (
                            <Box component="li" {...optionProps} key={key || `${option.id}-${option.path}`}>
                              <Stack spacing={0.25} sx={{ minWidth: 0, py: 0.5 }}>
                                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <Typography sx={{ fontWeight: 850, color: '#0f172a', overflowWrap: 'anywhere' }}>
                                    {option.name || '-'}
                                  </Typography>
                                  <Chip size="small" label={`ID: ${option.id || '-'}`} />
                                  {!option.isLeaf ? <Chip size="small" color="warning" label="Üst kategori" /> : null}
                                </Stack>
                                <Typography sx={{ color: '#64748b', fontSize: '0.8rem', overflowWrap: 'anywhere' }}>
                                  {option.path || option.name || '-'}
                                </Typography>
                              </Stack>
                            </Box>
                          );
                        }}
                      />
                      {categoryError ? (
                        <Alert severity="error" sx={{ borderRadius: 2, py: 0.5 }}>
                          {categoryError}
                        </Alert>
                      ) : null}
                </Stack>
              ) : (
                <Stack spacing={isInline ? 1.25 : 1.75}>
                  <Autocomplete
                    fullWidth
                    options={categoryOptions}
                    value={selectedCategory}
                    inputValue={categorySearch}
                    loading={categoryLoading}
                    filterOptions={(options) => options}
                    getOptionLabel={getCategoryOptionLabel}
                    isOptionEqualToValue={(option, selected) => formatCategoryId(option?.id) === formatCategoryId(selected?.id)}
                    getOptionDisabled={(option) => option?.isLeaf === false}
                    onInputChange={(event, nextInput, reason) => {
                      if (reason !== 'reset') setCategorySearch(nextInput);
                    }}
                    onChange={(event, nextValue) => handleCategorySelect(nextValue)}
                    noOptionsText={
                      categoryError
                        ? 'Arama yapılamadı'
                        : categorySearch.length < 2
                          ? 'Aramak için en az 2 karakter yazın.'
                          : 'Kategori bulunamadı.'
                    }
                    loadingText="Kategoriler aranıyor..."
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Trendyol kategorisi"
                        placeholder="Örn: bilezik, kolye, yüzük"
                        size={fieldSize}
                        sx={inputSx}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {categoryLoading ? <CircularProgress color="inherit" size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      return (
                        <Box component="li" {...optionProps} key={key || `${option.id}-${option.path}`}>
                          <Stack spacing={0.25} sx={{ minWidth: 0, py: 0.5 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography sx={{ fontWeight: 850, color: '#0f172a', overflowWrap: 'anywhere' }}>
                                {option.name || '-'}
                              </Typography>
                              <Chip size="small" label={`ID: ${option.id || '-'}`} />
                              {!option.isLeaf ? <Chip size="small" color="warning" label="Üst kategori" /> : null}
                            </Stack>
                            <Typography sx={{ color: '#64748b', fontSize: '0.8rem', overflowWrap: 'anywhere' }}>
                              {option.path || option.name || '-'}
                            </Typography>
                          </Stack>
                        </Box>
                      );
                    }}
                  />

                  {categoryError ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {categoryError}
                    </Alert>
                  ) : null}
                </Stack>
              )
            ) : null}
          </Stack>
    </Box>
  );

  const renderCompactAttributeCardBox = (attribute) => {
    const values = getAttributeValues(attribute);
    const selectedAttribute = selectedAttributes.find((item) => toText(item.attributeId) === attributeKey(attribute)) || {};
    const selectedValueId = toText(selectedAttribute.attributeValueId);
    const selectedCustomValue = toText(selectedAttribute.customAttributeValue);
    const isMissing = attribute.allowCustom ? !selectedCustomValue : !selectedValueId;

    return (
      <Box
        sx={{
          p: slimAttributeCards ? 0.52 : 1.15,
          height: '100%',
          borderRadius: 1.75,
          bgcolor: '#f8fafc',
          border: isMissing ? '1px solid #fde68a' : '1px solid #e2e8f0'
        }}
      >
        <Stack spacing={slimAttributeCards ? 0.46 : 0.85}>
          <Typography
            sx={{
              fontWeight: 800,
              color: '#0f172a',
              fontSize: slimAttributeCards ? '0.66rem' : '0.88rem',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              lineHeight: 1.22
            }}
          >
            {attribute.attributeName || 'Özellik'}
          </Typography>
          {attribute.allowCustom ? (
            <TextField
              fullWidth
              size="small"
              label="Değer"
              value={selectedCustomValue}
              onChange={(event) =>
                handleAttributeChange(attribute, {
                  attributeValueId: null,
                  attributeValueName: '',
                  customAttributeValue: event.target.value
                })
              }
              placeholder="Yazın"
              error={isMissing}
              helperText={slimAttributeCards ? (isMissing ? 'Zorunlu' : '') : isMissing ? 'Zorunlu' : ' '}
              FormHelperTextProps={{
                sx: {
                  m: 0,
                  mt: slimAttributeCards ? (isMissing ? 0.35 : 0) : undefined,
                  minHeight: slimAttributeCards ? 0 : isMissing ? undefined : '0.95em'
                }
              }}
              sx={{ ...inputSx, ...compactTrendyolInputSx }}
            />
          ) : values.length > 0 ? (
            <TextField
              select
              fullWidth
              size="small"
              label="Seçim"
              value={selectedValueId}
              onChange={(event) => {
                const selectedValue = values.find((item) => valueKey(item) === event.target.value);
                handleAttributeChange(attribute, {
                  attributeValueId: selectedValue?.id ?? event.target.value,
                  attributeValueName: toText(selectedValue?.name || selectedValue?.value),
                  customAttributeValue: ''
                });
              }}
              error={isMissing}
              helperText={slimAttributeCards ? (isMissing ? 'Zorunlu' : '') : isMissing ? 'Zorunlu' : ' '}
              FormHelperTextProps={{
                sx: {
                  m: 0,
                  mt: slimAttributeCards ? (isMissing ? 0.35 : 0) : undefined,
                  minHeight: slimAttributeCards ? 0 : isMissing ? undefined : '0.95em'
                }
              }}
              sx={{ ...inputSx, ...compactTrendyolInputSx }}
            >
              {values.map((item) => (
                <MenuItem key={valueKey(item)} value={valueKey(item)}>
                  {toText(item?.name || item?.value || item?.id)}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography sx={{ color: '#92400e', fontSize: slimAttributeCards ? '0.68rem' : '0.76rem', fontWeight: 600, lineHeight: 1.25 }}>
              {slimAttributeCards ? 'Hazır seçenek yok.' : 'Bu kategori için hazır seçenek yok; gönderim öncesi kontrol edin.'}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  };

  const trendyolCategorySearchStack = (
    <Stack spacing={urunEkleSimplifiedTrendyol ? 0.36 : 0.55} sx={{ width: '100%', minWidth: 0 }}>
      <Autocomplete
        fullWidth
        options={categoryOptions}
        value={selectedCategory}
        inputValue={categorySearch}
        loading={categoryLoading}
        filterOptions={(options) => options}
        getOptionLabel={getCategoryOptionLabel}
        isOptionEqualToValue={(option, selected) => formatCategoryId(option?.id) === formatCategoryId(selected?.id)}
        getOptionDisabled={(option) => option?.isLeaf === false}
        onInputChange={(event, nextInput, reason) => {
          if (reason !== 'reset') setCategorySearch(nextInput);
        }}
        onChange={(event, nextValue) => handleCategorySelect(nextValue)}
        noOptionsText={
          categoryError
            ? 'Arama yapılamadı'
            : categorySearch.length < 2
              ? 'En az 2 karakter yazın.'
              : 'Kategori bulunamadı.'
        }
        loadingText="Aranıyor..."
        renderInput={(params) => (
          <TextField
            {...params}
            label="Trendyol kategorisi"
            placeholder={urunEkleSimplifiedTrendyol ? 'Kategori ara...' : 'Örn: bilezik'}
            size={fieldSize}
            sx={{ ...inputSx, ...compactTrendyolInputSx }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {categoryLoading ? <CircularProgress color="inherit" size={urunEkleSimplifiedTrendyol ? 14 : 16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          if (urunEkleSimplifiedTrendyol) {
            const tail = [option.path && option.path !== option.name ? option.path : '', option.id ? `#${option.id}` : '']
              .filter(Boolean)
              .join(' · ');
            return (
              <Box component="li" {...optionProps} key={key || `${option.id}-${option.path}`} sx={{ py: 0.35, minHeight: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 650,
                    fontSize: '0.74rem',
                    lineHeight: 1.25,
                    color: '#0f172a',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%'
                  }}
                  title={[option.name, option.path, option.id].filter(Boolean).join(' · ')}
                >
                  <Box component="span" sx={{ fontWeight: 750 }}>
                    {option.name || '-'}
                  </Box>
                  {tail ? (
                    <Box component="span" sx={{ color: '#64748b', fontWeight: 600, ml: 0.35 }}>
                      {' '}
                      · {tail}
                    </Box>
                  ) : null}
                </Typography>
              </Box>
            );
          }
          return (
            <Box component="li" {...optionProps} key={key || `${option.id}-${option.path}`}>
              <Stack spacing={0.25} sx={{ minWidth: 0, py: 0.5 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontWeight: 850, color: '#0f172a', overflowWrap: 'anywhere', fontSize: '0.85rem' }}>
                    {option.name || '-'}
                  </Typography>
                  <Chip size="small" label={`ID: ${option.id || '-'}`} sx={{ height: 22 }} />
                  {!option.isLeaf ? <Chip size="small" color="warning" label="Üst kategori" sx={{ height: 22 }} /> : null}
                </Stack>
                <Typography sx={{ color: '#64748b', fontSize: '0.76rem', overflowWrap: 'anywhere' }}>
                  {option.path || option.name || '-'}
                </Typography>
              </Stack>
            </Box>
          );
        }}
      />
      {categoryError ? (
        <Alert severity="error" sx={{ borderRadius: 1.5, py: 0.35, '& .MuiAlert-message': { py: 0 } }}>
          {categoryError}
        </Alert>
      ) : null}
    </Stack>
  );

  const tyMandatoryAlertSx = urunEkleSimplifiedTrendyol
    ? {
        borderRadius: 1.25,
        py: 0,
        px: 0.85,
        minHeight: 32,
        maxHeight: 36,
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        '& .MuiAlert-icon': { mr: 0.65, py: 0, alignSelf: 'center', fontSize: '1rem' },
        '& .MuiAlert-message': {
          py: 0,
          overflow: 'hidden',
          fontSize: '0.72rem',
          fontWeight: 600,
          lineHeight: 1.2,
          width: '100%'
        }
      }
    : {
        borderRadius: 2,
        py: 0.45,
        '& .MuiAlert-message': { py: 0 }
      };

  const tyMandatoryLoadingAlertSx = urunEkleSimplifiedTrendyol
    ? { ...tyMandatoryAlertSx, maxHeight: 40, minHeight: 34 }
    : {
        borderRadius: 2,
        py: 0.45,
        '& .MuiAlert-message': { py: 0 }
      };

  const mergedMandatoryAttributesPane = (
    <Box
      sx={{
        flex: '1 1 0%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: urunEkleSimplifiedTrendyol ? 0.44 : 0.65,
        width: '100%'
      }}
    >
      {urunEkleSimplifiedTrendyol ? (
        <Typography sx={{ fontWeight: 850, fontSize: '0.82rem', color: '#0f172a', lineHeight: 1.2 }}>
          Zorunlu Trendyol Özellikleri
        </Typography>
      ) : (
        <Stack direction="row" spacing={0.65} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 850, fontSize: '0.74rem', color: '#475569' }}>Zorunlu Trendyol özellikleri</Typography>
          {selectedCategory?.id && categoryAttributes.length > 0 ? (
            <>
              <Chip size="small" color="warning" label={`${attributeSummary.total} zorunlu`} sx={{ height: 22, fontWeight: 800 }} />
              <Chip
                size="small"
                color={missingRequiredAttributes.length > 0 ? 'error' : 'success'}
                label={missingRequiredAttributes.length > 0 ? `${missingRequiredAttributes.length} eksik` : 'Tamamlandı'}
                sx={{ height: 22, fontWeight: 800 }}
              />
            </>
          ) : null}
        </Stack>
      )}

      {!selectedCategory?.id ? (
        <Alert severity="info" sx={tyMandatoryAlertSx}>
          {urunEkleSimplifiedTrendyol ? 'Kategori seçilince gerekli özellikler burada açılır.' : 'Kategori seçin; zorunlu alanlar bu satırda açılır.'}
        </Alert>
      ) : attributeLoading ? (
        <Alert severity="info" icon={<CircularProgress size={14} />} sx={tyMandatoryLoadingAlertSx}>
          Yükleniyor…
        </Alert>
      ) : attributeError ? (
        <Alert severity="error" sx={{ ...tyMandatoryAlertSx, maxHeight: 'none', alignItems: 'flex-start' }}>
          {attributeError}
        </Alert>
      ) : categoryAttributes.length === 0 ? (
        <Alert severity="info" sx={tyMandatoryAlertSx}>
          Bu kategori için zorunlu özellik bulunamadı.
        </Alert>
      ) : (
        <>
          {!urunEkleSimplifiedTrendyol && missingRequiredAttributes.length > 0 ? (
            <Typography sx={{ fontSize: '0.72rem', color: '#b45309', fontWeight: 700 }}>
              {missingRequiredAttributes.length} özellik için değer gerekli.
            </Typography>
          ) : null}
          {mandatoryAttributeChunks.map((chunk, rowIdx) => (
            <Box
              key={`ty-attr-grid-${rowIdx}`}
              className="ty-attr-grid-row"
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${chunk.length}, minmax(0, 1fr))`,
                columnGap: urunEkleSimplifiedTrendyol ? 0.62 : 0.75,
                width: '100%',
                minWidth: 0,
                alignItems: 'stretch',
                boxSizing: 'border-box',
                mb: rowIdx < mandatoryAttributeChunks.length - 1 ? (urunEkleSimplifiedTrendyol ? 0.52 : 0.75) : 0
              }}
            >
              {chunk.map((attribute, index) => (
                <Box
                  key={`ty-lane-${attribute.attributeId}-${rowIdx}-${index}`}
                  sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}
                >
                  {renderCompactAttributeCardBox(attribute)}
                </Box>
              ))}
            </Box>
          ))}
        </>
      )}
    </Box>
  );

  const gonderimFieldsForPortal = sections.find((s) => s.title === 'Gönderim Bilgileri')?.fields || [];

  return (
    <Stack spacing={stackGap}>
      {showValidation && !externalReadinessBanner ? (
        <TrendyolReadinessSummary
          readiness={readinessForBanner}
          isInline={isInline}
          showValidation={showValidation}
          denseBar={Boolean(urunEkleSimplifiedTrendyol)}
          emphasizedTitle={Boolean(urunEkleSimplifiedTrendyol)}
          suppressReadyDisclaimer={Boolean(urunEkleSimplifiedTrendyol)}
          expandMissingChips={Boolean(urunEkleSimplifiedTrendyol)}
        />
      ) : null}

      {trendyolCategorySearchPortalEl && !mergedTY
        ? createPortal(trendyolCategorySearchStack, trendyolCategorySearchPortalEl)
        : null}
      {trendyolShipmentFieldsPortalEl && !mergedTY && gonderimFieldsForPortal.length > 0
        ? createPortal(
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 1.25,
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              {gonderimFieldsForPortal.map((field) => (
                <Box key={field.name} sx={{ flex: '1 1 0', minWidth: 0, width: 0 }}>
                  {renderFieldInner(field, { gonderimTight: false, portalSmall: true })}
                </Box>
              ))}
            </Box>,
            trendyolShipmentFieldsPortalEl
          )
        : null}

      {mergedTY ? (
        <Box sx={mergedCategoryGonderimShellSx || sectionShellSx}>
          {trendyolCategorySearchPortalEl ? createPortal(trendyolCategorySearchStack, trendyolCategorySearchPortalEl) : null}
          {urunEkleSimplifiedTrendyol && !suppressBrandIdField ? (
            <TextField
              fullWidth
              size="small"
              label="Trendyol Marka ID"
              name="ty_brandId"
              value={normalized.ty_brandId || ''}
              onChange={handleFieldChange}
              placeholder="Örn: 12345"
              sx={{
                ...inputSx,
                ...compactTrendyolInputSx,
                mb: urunEkleSimplifiedTrendyol ? 0.35 : 1,
                '& .MuiInputLabel-root': {
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  fontSize: '0.74rem'
                }
              }}
            />
          ) : null}
          {mergedMandatoryAttributesPane}
        </Box>
      ) : null}

      {useTripleCatalogRow ? (
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          sx={{
            width: '100%',
            alignItems: 'stretch',
            '& > *': { flex: { lg: '1 1 0' }, minWidth: { lg: 0 }, display: 'flex' }
          }}
        >
          {tripleCatalogRowSections.map((section) => (
            <Box key={section.title} sx={{ minWidth: 0, flex: '1 1 0%' }}>
              {renderTrendyolFieldSectionCard(section)}
            </Box>
          ))}
        </Stack>
      ) : (
        sectionsForMap.map((section) => (
          <React.Fragment key={section.title}>{renderTrendyolFieldSectionCard(section)}</React.Fragment>
        ))
      )}


      {!inlineTyAttrsBesideCategory ? (
      <Box sx={sectionShellSx}>
        <Stack spacing={compactMandatoryAttrLayout ? (isInline ? 1.05 : 1.15) : isInline ? 1.35 : 2}>
          <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 950,
                    color: '#0f172a',
                    fontSize: isInline ? '0.9rem' : compactMandatoryAttrLayout ? '0.92rem' : '1rem'
                  }}
                >
                  Zorunlu Trendyol özellikleri
                </Typography>
                {!compactMandatoryAttrLayout ? (
                  <Typography sx={{ color: '#64748b', fontSize: isInline ? '0.76rem' : '0.84rem', mt: isInline ? 0.25 : 0.35, lineHeight: 1.45 }}>
                    Seçili kategori için Trendyol’un istediği zorunlu özellikler; değerler <b>ty_attributes</b> alanına kaydedilir.
                  </Typography>
                ) : null}
              </Box>
              {selectedCategory?.id && categoryAttributes.length > 0 ? (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                  <Chip size="small" color="warning" label={`${attributeSummary.total} zorunlu`} sx={{ fontWeight: 800 }} />
                  <Chip
                    size="small"
                    color={missingRequiredAttributes.length > 0 ? 'error' : 'success'}
                    label={missingRequiredAttributes.length > 0 ? `${missingRequiredAttributes.length} eksik` : 'Tamamlandı'}
                    sx={{ fontWeight: 800 }}
                  />
                  {!compactMandatoryAttrLayout ? (
                    <>
                      <Chip size="small" label={`${attributeSummary.valueListCount} liste değerli`} sx={{ fontWeight: 700 }} />
                      <Chip size="small" label={`${attributeSummary.customAllowedCount} özel giriş`} sx={{ fontWeight: 700 }} />
                    </>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          </Box>
          <Divider sx={compactMandatoryAttrLayout ? { my: 0.25 } : undefined} />

          {!selectedCategory?.id ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Zorunlu özellikleri görmek için önce Trendyol kategorisi seçin.
            </Alert>
          ) : attributeLoading ? (
            <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ borderRadius: 2 }}>
              Zorunlu özellikler yükleniyor...
            </Alert>
          ) : attributeError ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {attributeError}
            </Alert>
          ) : categoryAttributes.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Bu kategori için zorunlu özellik bulunamadı.
            </Alert>
          ) : (
            <Stack spacing={compactMandatoryAttrLayout ? 1.05 : 1.5}>
              {!compactMandatoryAttrLayout ? (
                <Alert
                  severity={missingRequiredAttributes.length > 0 ? 'warning' : 'success'}
                  sx={{ borderRadius: 2, bgcolor: missingRequiredAttributes.length > 0 ? '#fffbeb' : '#f0fdf4', border: missingRequiredAttributes.length > 0 ? '1px solid #fde68a' : '1px solid #bbf7d0' }}
                >
                  {missingRequiredAttributes.length > 0
                    ? `${missingRequiredAttributes.length} zorunlu özellik için değer bekleniyor.`
                    : 'Zorunlu özellik değerleri hazırlandı.'}{' '}
                  Bu ekranda ürün Trendyol’a gönderilmez.
                </Alert>
              ) : missingRequiredAttributes.length > 0 ? (
                <Typography sx={{ fontSize: '0.8rem', color: '#b45309', fontWeight: 700 }}>
                  {missingRequiredAttributes.length} özellik için değer gerekli.
                </Typography>
              ) : null}
              {compactMandatoryAttrLayout ? (
                <Stack spacing={isInline ? 1.15 : 1} sx={{ width: '100%', minWidth: 0 }}>
                  {mandatoryAttributeChunks.map((chunk, rowIdx) => (
                    <Box
                      key={`ty-attr-standalone-grid-${rowIdx}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${chunk.length}, minmax(0, 1fr))`,
                        columnGap: isInline ? 1.15 : 1,
                        rowGap: { xs: compactMandatoryAttributes ? 0.85 : 0, md: 0 },
                        width: '100%',
                        minWidth: 0,
                        alignItems: 'stretch',
                        boxSizing: 'border-box'
                      }}
                    >
                      {chunk.map((attribute, index) => (
                        <Box
                          key={`ty-lane-standalone-${attribute.attributeId}-${rowIdx}-${index}`}
                          sx={{ minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column' }}
                        >
                          {renderCompactAttributeCardBox(attribute)}
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Stack>
              ) : (
              <Grid container spacing={isInline ? 1.15 : 1.5}>
                {categoryAttributes.map((attribute, index) => {
                  const values = getAttributeValues(attribute);
                  const previewValues = getAttributeValuePreview(attribute);
                  const remainingValueCount = Math.max(values.length - previewValues.length, 0);
                  const selectedAttribute = selectedAttributes.find((item) => toText(item.attributeId) === attributeKey(attribute)) || {};
                  const selectedValueId = toText(selectedAttribute.attributeValueId);
                  const selectedCustomValue = toText(selectedAttribute.customAttributeValue);
                  const isMissing = attribute.allowCustom ? !selectedCustomValue : !selectedValueId;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={`${attribute.attributeId}-${attribute.attributeName || index}`}>
                      <Box
                        sx={{
                          p: isInline ? 1.15 : 1.65,
                          height: '100%',
                          borderRadius: isInline ? 2 : 2.5,
                          bgcolor: '#f8fafc',
                          border: isMissing ? '1px solid #fde68a' : '1px solid #bbf7d0',
                          boxShadow: isInline ? 'none' : '0 8px 22px rgba(15, 23, 42, 0.035)'
                        }}
                      >
                        <Stack spacing={1.2} sx={{ height: '100%' }}>
                          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 950, color: '#0f172a', fontSize: '0.92rem', overflowWrap: 'anywhere' }}>
                                {attribute.attributeName || 'Özellik'}
                              </Typography>
                              <Typography sx={{ color: '#64748b', fontSize: '0.76rem', mt: 0.25 }}>
                                Attribute ID: {attribute.attributeId || '-'}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              color={isMissing ? 'warning' : 'success'}
                              label={isMissing ? 'Eksik' : 'Seçildi'}
                              sx={{ fontWeight: 800 }}
                            />
                          </Stack>

                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            <Chip size="small" color="warning" label="Zorunlu" sx={{ fontWeight: 800 }} />
                            <Chip
                              size="small"
                              color={attribute.allowCustom ? 'success' : 'default'}
                              label={attribute.allowCustom ? 'Özel değer kabul eder' : 'Listeden seçilecek'}
                              sx={{ fontWeight: 700 }}
                            />
                            {attribute.varianter ? <Chip size="small" label="Varyant" sx={{ fontWeight: 700 }} /> : null}
                            {attribute.slicer ? <Chip size="small" label="Filtre" sx={{ fontWeight: 700 }} /> : null}
                          </Stack>

                          <Box
                            sx={{
                              p: 1.1,
                              borderRadius: 2,
                              bgcolor: '#fff',
                              border: '1px dashed #cbd5e1',
                              mt: 'auto'
                            }}
                          >
                            <Typography sx={{ color: '#475569', fontSize: '0.78rem', fontWeight: 800, mb: 0.65 }}>
                              Değer seçimi
                            </Typography>
                            {attribute.allowCustom ? (
                              <Stack spacing={0.75}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Manuel değer"
                                  value={selectedCustomValue}
                                  onChange={(event) =>
                                    handleAttributeChange(attribute, {
                                      attributeValueId: null,
                                      attributeValueName: '',
                                      customAttributeValue: event.target.value
                                    })
                                  }
                                  placeholder={`${attribute.attributeName || 'Özellik'} değeri girin`}
                                  error={isMissing}
                                  helperText={isMissing ? 'Bu zorunlu özellik için manuel değer girin.' : 'Manuel değer kayda hazırlanır.'}
                                  sx={inputSx}
                                />
                                {values.length > 0 ? (
                                  <Typography sx={{ color: '#64748b', fontSize: '0.74rem' }}>
                                    Trendyol ayrıca {values.length} hazır değer döndürdü; bu alan özel değer kabul ettiği için manuel giriş kullanılacak.
                                  </Typography>
                                ) : null}
                              </Stack>
                            ) : values.length > 0 ? (
                              <Stack spacing={0.75}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label="Değer seç"
                                  value={selectedValueId}
                                  onChange={(event) => {
                                    const selectedValue = values.find((item) => valueKey(item) === event.target.value);
                                    handleAttributeChange(attribute, {
                                      attributeValueId: selectedValue?.id ?? event.target.value,
                                      attributeValueName: toText(selectedValue?.name || selectedValue?.value),
                                      customAttributeValue: ''
                                    });
                                  }}
                                  error={isMissing}
                                  helperText={isMissing ? 'Bu zorunlu özellik için değer seçin.' : 'Seçilen değer ty_attributes içine kaydedilir.'}
                                  sx={inputSx}
                                >
                                  {values.map((item) => (
                                    <MenuItem key={valueKey(item)} value={valueKey(item)}>
                                      {toText(item?.name || item?.value || item?.id)}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                  {previewValues.map((item) => (
                                    <Chip key={item} size="small" variant="outlined" label={item} sx={{ maxWidth: '100%' }} />
                                  ))}
                                  {remainingValueCount > 0 ? (
                                    <Chip size="small" variant="outlined" label={`+${remainingValueCount} değer`} />
                                  ) : null}
                                </Stack>
                              </Stack>
                            ) : (
                              <Typography sx={{ color: '#92400e', fontSize: '0.76rem', fontWeight: 700 }}>
                                Hazır değer listesi gelmedi ve özel giriş kapalı. Gönderim fazından önce bu alan ayrıca kontrol edilmeli.
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
              )}
            </Stack>
          )}
        </Stack>
      </Box>
      ) : null}

      {!hideAuto && !hideTrendyolDescription ? (
        <Box sx={sectionShellSx}>
          <Stack spacing={isInline ? 1.35 : 2.25}>
            <Box>
              <Typography sx={{ fontWeight: 950, color: '#0f172a', fontSize: isInline ? '0.9rem' : '1rem' }}>Trendyol Açıklaması</Typography>
              {!isInline ? (
                <Typography sx={{ color: '#64748b', fontSize: '0.84rem', mt: 0.35, lineHeight: 1.45 }}>
                  Ürünün Trendyol’da görünecek açıklama metni.
                </Typography>
              ) : (
                <Typography sx={{ color: '#64748b', fontSize: '0.76rem', mt: 0.25, lineHeight: 1.4 }}>
                  Pazaryeri açıklaması (ty_aciklama).
                </Typography>
              )}
            </Box>
            <Divider sx={{ my: isInline ? -0.25 : 0 }} />
            <TextField
              fullWidth
              multiline
              size={fieldSize}
              minRows={isInline ? 5 : 8}
              label="Trendyol açıklaması"
              name="ty_aciklama"
              value={normalized.ty_aciklama || ''}
              onChange={handleFieldChange}
              placeholder={defaults.ty_aciklama || ''}
              sx={{
                ...inputSx,
                '& .MuiInputBase-root': {
                  minHeight: isInline ? { xs: 120, sm: 140 } : { xs: 180, sm: 210 },
                  alignItems: 'flex-start'
                }
              }}
            />
          </Stack>
        </Box>
      ) : null}

      {!isInline && !hideAuto ? (
        <Typography
          variant="body2"
          sx={{
            color: '#475569',
            lineHeight: 1.55,
            bgcolor: '#f1f5f9',
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            px: 1.5,
            py: 1.25
          }}
        >
          Zorunlu özellik değer seçimi sonraki aşamada otomatik hale getirilecektir.
        </Typography>
      ) : null}
    </Stack>
  );
}

export default TrendyolProductInfoForm;
